import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { getCategoryAttributes, getAttributeSpecifications } from '../../../app/api';
import { normalizeAttributeSpec } from '../public/categoryUtils';
import AttributeSpecificationsManageDialog from './AttributeSpecificationsManageDialog';
import { useTranslation } from '../../../app/LanguageContext';

export default function CategoryAttributesEditor({
  categoryId,
  direct = [],
  onDirectChange,
  disabled = false,
}) {
  const { translate } = useTranslation();
  const [inherited, setInherited] = useState([]);
  const [available, setAvailable] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);

  const loadSpecs = useCallback(async () => {
    try {
      const specsRes = await getAttributeSpecifications({ limit: 200 });
      const specs = (specsRes.data?.attributes ?? specsRes.data ?? [])
        .map(normalizeAttributeSpec)
        .filter(Boolean);
      setAvailable(specs);
    } catch {
      setAvailable([]);
    }
  }, []);

  const loadInherited = useCallback(async () => {
    if (!categoryId) {
      setInherited([]);
      return;
    }
    try {
      const attrRes = await getCategoryAttributes(categoryId);
      setInherited(attrRes.data?.inherited ?? []);
    } catch {
      setInherited([]);
    }
  }, [categoryId]);

  useEffect(() => {
    void loadInherited();
    void loadSpecs();
  }, [categoryId, loadInherited, loadSpecs]);

  const assignedIds = new Set(direct.map((d) => d.attribute_specification_id));
  const pickable = available.filter((s) => !assignedIds.has(s.id));

  const handleAdd = (spec) => {
    onDirectChange?.([
      ...direct,
      {
        attribute_specification_id: spec.id,
        name: spec.name,
        type: spec.type,
        is_required: false,
        values: (spec.values || []).map((v) => v.value),
      },
    ]);
  };

  const handleRemove = (attrId) => {
    onDirectChange?.(direct.filter((d) => d.attribute_specification_id !== attrId));
  };

  const handleToggleRequired = (attrId) => {
    onDirectChange?.(
      direct.map((d) =>
        d.attribute_specification_id === attrId ? { ...d, is_required: !d.is_required } : d,
      ),
    );
  };

  const handleSpecsChanged = async () => {
    await loadSpecs();
    await loadInherited();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1">{translate('categories.attributes_direct')}</Typography>
        <Button size="small" startIcon={<SettingsOutlinedIcon />} onClick={() => setManageOpen(true)}>
          {translate('categories.attributes_manage')}
        </Button>
      </Box>
      <List dense>
        {direct.map((item) => (
          <ListItem
            key={item.attribute_specification_id}
            secondaryAction={
              <IconButton
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => handleRemove(item.attribute_specification_id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemText primary={item.name} secondary={item.type} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(item.is_required)}
                  onChange={() => handleToggleRequired(item.attribute_specification_id)}
                  disabled={disabled}
                />
              }
              label={translate('categories.attribute_required')}
            />
          </ListItem>
        ))}
      </List>
      {pickable.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {translate('categories.attributes_add')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {pickable.map((spec) => (
              <Button key={spec.id} size="small" variant="outlined" disabled={disabled} onClick={() => handleAdd(spec)}>
                {spec.name}
              </Button>
            ))}
          </Box>
        </Box>
      )}
      {inherited.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>{translate('categories.attributes_inherited')}</Typography>
          {inherited.map((item) => (
            <Typography key={item.id ?? item.attribute_specification_id} variant="body2" color="text.secondary">
              {item.name} ({item.type})
            </Typography>
          ))}
        </Box>
      )}
      <AttributeSpecificationsManageDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={handleSpecsChanged}
      />
    </Box>
  );
}
